import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, EventEmitter, inject, Output, OnInit } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { LoaderComponent } from "../../../general/components/loader/loader.component";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { HapticsService } from "../../../general/services/haptics.service";
import { AdminTemplateSendRequest, EmailTemplate } from "../../models/admin-template-email-request";
import { AdminService } from "../../services/admin.service";
import { SnackBarService } from "../../../services/snackbar.service";
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from "ngx-mask";
import { MatDialogRef } from "@angular/material/dialog";
import { AuthService } from "../../../../core/services/auth.service";
import { User } from "../../../user/models/user.model";
import { SalesAgent } from "../../../../features/sales/models/sales-agent.model";
import { SalesService } from "../../../../features/sales/services/sales-agent.service";
import { SuccessError } from "../../../general/components/success-error/success-error";

@Component({
  selector: 'app-template-send',
  standalone: true,
  providers: [provideNgxMask()],
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    ReactiveFormsModule,
    MatFormField,
    MatInputModule,
    MatLabel,
    LoaderComponent,
    NgxMaskDirective,
    NgxMaskPipe,
    SuccessError
  ],
  templateUrl: './template-send.html',
  styleUrl: './template-send.scss',
})
export class TemplateSendComponent implements OnInit {
    private fb = inject(FormBuilder);
    private adminService = inject(AdminService);
    private salesService = inject(SalesService);
    private auth = inject(AuthService);
    private snackbar = inject(SnackBarService);
    private cdr = inject(ChangeDetectorRef);
    private haptics = inject(HapticsService);

    private dialogRef = inject(MatDialogRef<TemplateSendComponent>, { optional: true });
    @Output() cancel = new EventEmitter<void>();
    @Output() success = new EventEmitter<any>();
    tempForm: FormGroup;
    loading = false;
    submitted = false;
    successful = false;
    user: User | null = null;
    agent: SalesAgent | null = null;
    templates: EmailTemplate[] = [
        { id: '0', name: 'Stop paying booking fees', description: 'Brief message to reach out to potential client', icon: 'block' },
        { id: '1', name: 'Save $X per property annually', description: 'Sent after a successful reservation', icon: 'percent_discount' },
        { id: '2', name: 'Quick Thought', description: 'Marketing blast for properties', icon: 'campaign' },
        { id: '3', name: 'Helping managers reduce platform dependency', description: 'Reduces reliance on expensive third-party platforms', icon: 'pinch_zoom_in'},
        { id: '4', name: 'Respond to Sales Application', description: 'Quick response with instructions for sales applicants', icon: 'reply'}
    ];

    constructor() {
        this.tempForm = this.fb.group({
            templateId: ['', Validators.required],
            senderName: ['', Validators.required],
            senderTitle: ['', Validators.required],
            senderEmail: ['', [Validators.required, Validators.email]],
            senderPhone: ['', [Validators.required]],
            sendToEmail: ['', [Validators.required, Validators.email]],
            sendToName: ['', Validators.required],
            subjectInput: ['', Validators.required],
            numberOfProperties: ['']
        });
    }

    get selectedTemplateName(): string {
        const id = this.tempForm.get('templateId')?.value;
        const template = this.templates.find(t => t.id === id);
        return template ? template.name : '';
    }

    ngOnInit() {
        this.user = this.auth.currentUser();
        
        if (this.user?.id) {
            this.salesService.getSalesAgentByUserId(this.user.id).subscribe({
                next: (agent) => {
                    this.agent = agent;
                    
                    // Patch the form only AFTER we have the agent data
                    this.tempForm.patchValue({
                        senderName: `${this.user?.firstName} ${this.user?.lastName}`,
                        senderEmail: 'sales@horizinns.com',
                        senderTitle: 'Co-Owner',
                        senderPhone: agent?.phoneNumber || ''
                    });
                    const phoneControl = this.tempForm.get('senderPhone');
                    if (phoneControl) {
                        phoneControl.markAsDirty();
                        phoneControl.markAsTouched();
                        phoneControl.updateValueAndValidity();
                    }
                    this.cdr.detectChanges();
                },
                error: (err) => {
                    console.error('Failed to load agent', err);
                }
            });
        }
    }

    onCancel() {
        if (this.dialogRef) {
            this.dialogRef.close();
            
        } else {
            this.cancel.emit();
        }
    }

    submit() {
        console.log('form', this.tempForm);
        this.haptics.click();
        this.loading = true;
        this.submitted = true;

        if (this.tempForm.valid) {
            this.submitted = true;
            this.loading = true;

            const numProp = this.tempForm.value.numberOfProperties ?? 0;
            // Use .rawValue or a typed getter to simplify data extraction
            const request: AdminTemplateSendRequest = {
                senderName: this.tempForm.value.senderName,
                senderEmail: this.tempForm.value.senderEmail,
                senderTitle: this.tempForm.value.senderTitle,
                senderPhone: this.tempForm.value.senderPhone,
                sendToName: this.tempForm.value.sendToName,
                sendToEmail: this.tempForm.value.sendToEmail,
                subjectInput: this.tempForm.value.subjectInput,
                templateId: this.tempForm.value.templateId,
                numberOfProperties: numProp
            };
            this.adminService.sendTemplate(request).subscribe({
                next: (response: any) => {
                    this.loading = false;
                    this.successful = true;
                    this.snackbar.open(
                        `✅ Email Sent Successfully`,
                        ' ',
                        { duration: 5000, panelClass: ['success-snackbar'], horizontalPosition: 'center', verticalPosition: 'top' }
                    );
                    this.success.emit({ status: 'sent', data: response });
                    this.cdr.detectChanges();
                    
                    //this.dialogRef?.close({ success: true, email: 'sent' });
                },
                error: (err) => {
                    this.loading = false;
                    this.successful = false;
                    console.error('Submission failed', err);
                    this.snackbar.error(err.error?.message || err.error || 'Registration failed');
                    this.cdr.detectChanges();
                }
            });
        } else {
            // Mark all fields as touched to trigger validation UI
            this.loading = false;
            this.submitted = false;
            this.tempForm.markAllAsTouched();
        }
    }

    submitAnother() {
        this.haptics.click();

        this.submitted = false;
        this.loading = false;
        this.successful = false;

        this.tempForm.reset();
        this.tempForm.patchValue({
            senderName: `${this.user?.firstName} ${this.user?.lastName}`,
            senderEmail: 'sales@horizinns.com',
            senderTitle: 'Co-Owner',
            senderPhone: this.agent?.phoneNumber || ''
        });
        const phoneControl = this.tempForm.get('senderPhone');
        if (phoneControl) {
            phoneControl.markAsDirty();
            phoneControl.markAsTouched();
            phoneControl.updateValueAndValidity();
        }
        this.cdr.detectChanges();
    }
}